import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Link } from 'react-router-dom'
import {
  Package, Monitor, Database, Users, Building2, Heart, BookOpen, Shield,
  Cpu, Smartphone, ArrowRight, Sparkles, Layers, Server
} from 'lucide-react'
import { productsSEO } from '@constants/seo'

const softwareProducts = [
  {
    icon: Monitor,
    name: 'Project Tracking App',
    description: 'Real-time project monitoring, milestone tracking, and progress reporting for government and institutional projects.',
    features: ['Dashboard Analytics', 'Milestone Tracking', 'Resource Allocation', 'Progress Reports'],
    color: '#6366f1',
  },
  {
    icon: Database,
    name: 'Budget Record System (BRS)',
    description: 'Comprehensive financial management, budget allocation, expense tracking, and audit compliance.',
    features: ['Budget Planning', 'Expense Tracking', 'Financial Reports', 'Audit Trail'],
    color: '#8b5cf6',
  },
  {
    icon: Shield,
    name: 'Smart Card Application',
    description: 'Secure identification, access management, and attendance tracking with biometric integration.',
    features: ['Biometric Auth', 'Access Control', 'Attendance Tracking', 'Visitor Management'],
    color: '#ec4899',
  },
  {
    icon: Users,
    name: 'Accounting & Finance Suite',
    description: 'Complete financial operations including ledger management, invoicing, taxation, and reporting.',
    features: ['General Ledger', 'Invoicing', 'Tax Management', 'Financial Statements'],
    color: '#f59e0b',
  },
  {
    icon: Building2,
    name: 'Construction & Project Management',
    description: 'End-to-end project lifecycle management for construction and infrastructure projects.',
    features: ['Project Planning', 'Resource Management', 'Timeline Tracking', 'Cost Control'],
    color: '#10b981',
  },
  {
    icon: Users,
    name: 'Admin, HR & Inventory System',
    description: 'Integrated HR management, payroll, recruitment, and inventory tracking in one platform.',
    features: ['Employee Records', 'Payroll Management', 'Inventory Tracking', 'Leave Management'],
    color: '#06b6d4',
  },
  {
    icon: BookOpen,
    name: 'Educational Platform',
    description: 'Learning management system with student information, course management, and assessments.',
    features: ['Student Portal', 'Course Management', 'Online Exams', 'Progress Tracking'],
    color: '#f97316',
  },
  {
    icon: Smartphone,
    name: 'Mobile Applications',
    description: 'Custom mobile apps for Android and iOS platforms with offline support and real-time sync.',
    features: ['Cross-Platform', 'Offline Support', 'Push Notifications', 'Real-time Sync'],
    color: '#84cc16',
  },
]

const infrastructureProducts = [
  {
    icon: Monitor,
    name: 'IT Labs Setup',
    description: 'Modern computer laboratories with latest hardware, networking, and educational software.',
    features: ['High-Performance PCs', 'Network Infrastructure', 'Educational Software', 'Technical Support'],
    color: '#6366f1',
  },
  {
    icon: Cpu,
    name: 'Math & Robotics Labs',
    description: 'STEM-focused learning infrastructure with robotics kits, programming tools, and experiment stations.',
    features: ['Robotics Kits', 'Programming Tools', 'Experiment Stations', 'STEM Curriculum'],
    color: '#8b5cf6',
  },
  {
    icon: BookOpen,
    name: 'Digital Library System',
    description: 'E-library with digital catalog, e-books, research papers, and integrated management system.',
    features: ['Digital Catalog', 'E-Books Access', 'Research Portal', 'User Management'],
    color: '#ec4899',
  },
  {
    icon: Layers,
    name: 'Smart Classroom Solutions',
    description: 'Interactive displays, digital teaching tools, and multimedia learning environments.',
    features: ['Interactive Displays', 'Digital Whiteboards', 'Audio Systems', 'Content Library'],
    color: '#f59e0b',
  },
  {
    icon: Shield,
    name: 'Security & Surveillance',
    description: 'Comprehensive CCTV systems, access control, and security monitoring solutions.',
    features: ['HD Cameras', 'Access Control', '24/7 Monitoring', 'Mobile Alerts'],
    color: '#10b981',
  },
  {
    icon: Server,
    name: 'Server & Network Infrastructure',
    description: 'Enterprise-grade servers, data centers, and network architecture for government institutions.',
    features: ['Server Setup', 'Data Center', 'Network Design', 'Backup Solutions'],
    color: '#06b6d4',
  },
  {
    icon: Package,
    name: 'Office Furniture',
    description: 'Premium office furniture including desks, chairs, workstations, and storage solutions.',
    features: ['Ergonomic Design', 'Premium Quality', 'Custom Configurations', 'Bulk Orders'],
    color: '#f97316',
  },
  {
    icon: Layers,
    name: 'Compactors & Storage',
    description: 'High-density mobile storage systems for files, documents, and archival needs.',
    features: ['Space Efficient', 'Mobile Systems', 'Lockable Units', 'Easy Access'],
    color: '#84cc16',
  },
  {
    icon: Heart,
    name: 'Hospital Furniture',
    description: 'Medical-grade furniture for hospitals, clinics, and healthcare facilities.',
    features: ['Medical Grade', 'Easy Sanitization', 'Patient Comfort', 'Durable Build'],
    color: '#14b8a6',
  },
  {
    icon: Package,
    name: 'Laboratory Equipment',
    description: 'Science lab equipment, instruments, and safety gear for educational institutions.',
    features: ['Lab Instruments', 'Safety Equipment', 'Chemical Storage', 'Experiment Kits'],
    color: '#3b82f6',
  },
]

export const ProductsPage = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })

  return (
    <>
      <Helmet>
        <title>Software Products & Infrastructure Solutions | Government Technology Products</title>
        <meta name="description" content="Explore 18+ enterprise products: Project Tracking App, Budget Record System, Smart Cards, IT Labs, Smart Classrooms, CCTV, Office Furniture, Hospital Furniture. ISO certified solutions for government, education & healthcare." />
        <meta name="keywords" content="project tracking software, budget management system, smart card application, IT lab setup, smart classroom, CCTV surveillance, office furniture Pune, hospital furniture, school furniture, infrastructure products, enterprise software, government technology products" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <link rel="canonical" href="https://www.eonixa.in/products" />

        {/* Open Graph */}
        <meta property="og:title" content="Software Products & Infrastructure Solutions | Eonixa Dynamics" />
        <meta property="og:description" content="18+ enterprise products for government & institutions: Software (Project Tracking, Budget Management, HR & Inventory) & Infrastructure (IT Labs, Smart Classrooms, Security Systems, Furniture)" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.eonixa.in/products" />
        <meta property="og:image" content="https://www.eonixa.in/og-products.jpg" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Software Products & Infrastructure Solutions | Eonixa Dynamics" />
        <meta name="twitter:description" content="18+ enterprise products for government & institutions. ISO certified solutions for education, healthcare & government sectors." />

        {/* Structured Data - Products */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Eonixa Dynamics Products",
            "description": "Enterprise software and infrastructure products for government and institutional sectors",
            "numberOfItems": productsSEO.software.length + productsSEO.infrastructure.length,
            "itemListElement": [
              ...productsSEO.software.map((product, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "item": {
                  "@type": "Product",
                  "name": product.name,
                  "description": product.description,
                  "category": product.category,
                  "brand": { "@type": "Brand", "name": "Eonixa Dynamics" }
                }
              })),
              ...productsSEO.infrastructure.map((product, index) => ({
                "@type": "ListItem",
                "position": productsSEO.software.length + index + 1,
                "item": {
                  "@type": "Product",
                  "name": product.name,
                  "description": product.description,
                  "category": product.category,
                  "brand": { "@type": "Brand", "name": "Eonixa Dynamics" }
                }
              }))
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[50vh] overflow-hidden pt-24">
        <div className="container-enterprise relative z-10 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                border: '1px solid rgba(99, 102, 241, 0.25)',
                boxShadow: '0 0 30px rgba(99, 102, 241, 0.1)',
              }}
            >
              <Package className="w-5 h-5 text-primary" />
              <span className="text-sm text-slate-300">Product Portfolio</span>
            </motion.div>
            <h1 className="mb-6 text-4xl font-bold text-white sm:text-5xl">
              Our <span className="gradient-text">Products</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto leading-relaxed">
              Comprehensive software solutions and infrastructure products designed for government, education, healthcare, and enterprise sectors.
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 max-w-4xl mx-auto"
          >
            {[
              { value: '8+', label: 'Software Products' },
              { value: '10+', label: 'Infrastructure Products' },
              { value: '50+', label: 'Institutions Served' },
              { value: '100%', label: 'Quality Assured' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                className="text-center p-4 rounded-2xl"
                style={{
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                }}
              >
                <p className="text-2xl font-bold text-white mb-1">{stat.value}</p>
                <p className="text-sm text-slate-400">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Software Products */}
      <section ref={ref} className="py-24 relative overflow-hidden">
        <div className="container-enterprise relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.3 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                border: '1px solid rgba(99, 102, 241, 0.25)',
                boxShadow: '0 0 30px rgba(99, 102, 241, 0.1)',
              }}
            >
              <Monitor className="w-4 h-4 text-primary" />
              <span className="text-sm text-slate-300">Software Solutions</span>
            </motion.div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Software Products</h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Enterprise software solutions designed for government and institutional efficiency
            </p>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {softwareProducts.map((product, index) => {
              const Icon = product.icon
              return (
                <motion.div
                  key={product.name}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="group relative"
                >
                  <div className="rounded-2xl p-6 h-full transition-all duration-300 hover:translate-y-[-4px]"
                    style={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110"
                      style={{
                        background: `${product.color}20`,
                        border: `1px solid ${product.color}40`,
                      }}
                    >
                      <Icon className="w-7 h-7" style={{ color: product.color }} />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-primary transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-sm text-slate-400 mb-4 leading-relaxed">{product.description}</p>
                    <div className="space-y-2">
                      {product.features.slice(0, 3).map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full" style={{ background: product.color }} />
                          <span className="text-xs text-slate-500">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Infrastructure Products */}
      <section className="py-24 relative overflow-hidden">
        <div className="container-enterprise relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.3 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)',
                border: '1px solid rgba(16, 185, 129, 0.25)',
                boxShadow: '0 0 30px rgba(16, 185, 129, 0.1)',
              }}
            >
              <Server className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-slate-300">Infrastructure Solutions</span>
            </motion.div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Infrastructure Products</h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Physical infrastructure and equipment for modern institutions
            </p>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {infrastructureProducts.map((product, index) => {
              const Icon = product.icon
              return (
                <motion.div
                  key={product.name}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="group relative"
                >
                  <div className="rounded-2xl p-6 h-full transition-all duration-300 hover:translate-y-[-4px]"
                    style={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.05) 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110"
                      style={{
                        background: `${product.color}20`,
                        border: `1px solid ${product.color}40`,
                      }}
                    >
                      <Icon className="w-7 h-7" style={{ color: product.color }} />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-emerald-400 transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-sm text-slate-400 mb-4 leading-relaxed">{product.description}</p>
                    <div className="space-y-2">
                      {product.features.slice(0, 3).map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full" style={{ background: product.color }} />
                          <span className="text-xs text-slate-500">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 relative overflow-hidden">
        <div className="container-enterprise relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="text-3xl font-bold text-white mb-4">Need a Custom Solution?</h2>
            <p className="text-lg text-slate-400 mb-8">
              We design and develop custom products tailored to your specific institutional requirements.
            </p>
            <Link
              to="/contact"
              className="btn-glow inline-flex items-center gap-2 px-8 py-4 text-white font-semibold rounded-xl"
            >
              <Sparkles className="w-5 h-5" />
              <span>Request a Quote</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default ProductsPage